/* eslint-disable no-console -- This file is the admin logger sink; `console.*` is the output mechanism. */
import {
    ComponentErrorLogData,
    FrontendAPILogData,
    FrontendLogContext,
    FrontendLogMeta,
    IFrontendLogger,
    UserActionLogData,
    UserTransactionLogData,
} from "./IFrontendLogger";
import {
    TELEMETRY_SOURCE,
    enqueueFrontendTelemetryRecord,
    flushFrontendTelemetry,
    getFrontendSessionLogStorageKey,
    type FrontendTelemetryError,
    type FrontendTelemetryLogRecord,
} from "./frontendTelemetrySink";

type InternalLogMeta =
    | FrontendLogMeta
    | UserTransactionLogData
    | ComponentErrorLogData
    | UserActionLogData
    | FrontendAPILogData;

const readEnvValue = (key: string): string | undefined =>
    (import.meta.env as Record<string, string | undefined>)[key];

const resolveDeployStage = (): string => {
    const configured = readEnvValue("VITE_DEPLOY_STAGE")?.trim();
    if (configured) return configured;

    if (typeof window === "undefined") {
        return "server";
    }

    const hostname = window.location.hostname.toLowerCase();
    if (
        hostname === "localhost" ||
        hostname === "127.0.0.1" ||
        hostname === "0.0.0.0" ||
        hostname.endsWith(".local")
    ) {
        return "localhost";
    }

    if (
        hostname.includes("preview") ||
        hostname.includes("staging") ||
        hostname.includes("dev") ||
        hostname.includes("test")
    ) {
        return "staging";
    }

    return "production";
};

let globalContext: FrontendLogContext = {
    service: readEnvValue("VITE_LOGGING_SERVICE_NAME") ?? TELEMETRY_SOURCE,
    environment:
        import.meta.env.MODE === "development" ? "development" : "production",
    deployStage: resolveDeployStage(),
    buildVersion: readEnvValue("VITE_BUILD_VERSION") ?? "unknown",
    ...(readEnvValue("VITE_BUILD_SHA")
        ? { buildSha: readEnvValue("VITE_BUILD_SHA") }
        : {}),
};

export const setAdminLoggerUserId = (userId?: string): void => {
    if (userId) {
        globalContext = { ...globalContext, userId };
        return;
    }

    const { userId: _ignored, ...rest } = globalContext;
    globalContext = rest;
};

/**
 * Minimal, console-backed logger for HellocashflowAdmin.
 *
 * @remarks
 * This is intentionally simple: it gives us a consistent, typed logging surface (levels + contextual `child()`)
 * without introducing a full telemetry pipeline for the admin app.
 *
 * Logging safety:
 * - Do not log secrets/PII (tokens, raw user objects, full request/response payloads).
 * - Prefer IDs + small metadata objects.
 */
class ConsoleLogger implements IFrontendLogger {
    private context: FrontendLogContext = {};

    constructor(context: FrontendLogContext = {}) {
        this.context = context;
    }

    emerg(message: string, meta?: FrontendLogMeta) {
        this.log("emerg", message, meta);
    }
    alert(message: string, meta?: FrontendLogMeta) {
        this.log("alert", message, meta);
    }
    crit(message: string, meta?: FrontendLogMeta) {
        this.log("crit", message, meta);
    }
    error(message: string, error?: Error, meta?: FrontendLogMeta) {
        this.log("error", message, meta, error);
    }
    warning(message: string, meta?: FrontendLogMeta) {
        this.log("warning", message, meta);
    }
    warn(message: string, meta?: FrontendLogMeta) {
        this.log("warn", message, meta);
    }
    notice(message: string, meta?: FrontendLogMeta) {
        this.log("notice", message, meta);
    }
    info(message: string, meta?: FrontendLogMeta) {
        this.log("info", message, meta);
    }
    debug(message: string, meta?: FrontendLogMeta) {
        this.log("debug", message, meta);
    }
    trace(message: string, meta?: FrontendLogMeta) {
        this.log("trace", message, meta);
    }

    logUserTransaction(
        message: string,
        transactionData: UserTransactionLogData,
    ) {
        this.log("info", `Transaction: ${message}`, transactionData);
    }
    logComponentError(message: string, componentData: ComponentErrorLogData) {
        this.log("error", `Component Error: ${message}`, componentData);
    }
    logUserAction(message: string, actionData: UserActionLogData) {
        this.log("info", `User Action: ${message}`, actionData);
    }
    logAPICall(message: string, apiData: FrontendAPILogData) {
        this.log("info", `API Call: ${message}`, apiData);
    }

    child(context: FrontendLogContext): IFrontendLogger {
        return new ConsoleLogger({ ...this.context, ...context });
    }
    withCorrelationId(correlationId: string): IFrontendLogger {
        return this.child({ correlationId });
    }
    withOrganisation(orgId: string, orgName?: string): IFrontendLogger {
        return this.child({ organizationId: orgId, organizationName: orgName });
    }
    withUser(userId: string, userEmail?: string): IFrontendLogger {
        return this.child({ userId, userEmail });
    }
    withComponent(componentName: string): IFrontendLogger {
        return this.child({ component: componentName });
    }

    private shouldLog(level: string): boolean {
        // Keep this aligned to `VITE_LOG_LEVEL` semantics in `docs/LOGGING.md`.
        // Default is `warn` unless explicitly overridden.
        const severityMap: Record<string, number> = {
            // Canonical levels (expected by backend telemetry ingestion)
            fatal: 0,
            error: 1,
            warn: 2,
            warning: 2,
            info: 3,
            debug: 4,
            trace: 5,

            // Backend-style 8-level aliases supported by this logger API
            emerg: 0,
            alert: 0,
            crit: 0,
            notice: 3,
        };

        const normalizedLevel = level.toLowerCase();
        const severity = severityMap[normalizedLevel] ?? severityMap.info;

        const configuredLevel = readEnvValue("VITE_LOG_LEVEL")?.toLowerCase();

        const threshold =
            configuredLevel && configuredLevel in severityMap
                ? severityMap[configuredLevel]
                : severityMap.warn;

        return severity <= threshold;
    }

    private toTelemetryError(error?: Error): FrontendTelemetryError | undefined {
        if (!error) {
            return undefined;
        }

        return {
            name: error.name,
            message: error.message,
            stack: error.stack,
        };
    }

    private toTelemetryRecord(
        level: string,
        message: string,
        data: FrontendLogContext,
        timestamp: string,
        error?: Error,
    ): FrontendTelemetryLogRecord {
        const normalizedLevel = level.toLowerCase();
        const telemetryLevel =
            normalizedLevel === "emerg" ||
            normalizedLevel === "alert" ||
            normalizedLevel === "crit"
                ? "fatal"
                : normalizedLevel === "notice"
                  ? "info"
                  : normalizedLevel === "warning"
                    ? "warn"
                    : normalizedLevel;

        return {
            timestamp,
            level: telemetryLevel,
            severityText: telemetryLevel.toUpperCase(),
            message,
            attributes: data,
            error: this.toTelemetryError(error),
            source: TELEMETRY_SOURCE,
        };
    }

    private log(
        level: string,
        message: string,
        meta?: InternalLogMeta,
        error?: Error,
    ) {
        if (!this.shouldLog(level)) {
            return;
        }

        const timestamp = new Date().toISOString();
        const data: FrontendLogContext = {
            ...globalContext,
            ...this.context,
            ...meta,
        };
        const telemetryRecord = this.toTelemetryRecord(
            level,
            message,
            data,
            timestamp,
            error,
        );
        const formattedMessage = `[${level.toUpperCase()}] ${message}`;

        enqueueFrontendTelemetryRecord(telemetryRecord);

        if (error) {
            console.error(formattedMessage, error, { ...data, timestamp });
            return;
        }

        if (
            telemetryRecord.level === "fatal" ||
            telemetryRecord.level === "error"
        ) {
            console.error(formattedMessage, { ...data, timestamp });
            return;
        }

        if (telemetryRecord.level === "warn") {
            console.warn(formattedMessage, { ...data, timestamp });
            return;
        }

        console.log(formattedMessage, { ...data, timestamp });
    }
}

class FrontendLoggerFactory {
    /**
     * Returns a logger instance for use in React components, hooks, and services.
     *
     * @remarks
     * Prefer scoping immediately via `withComponent()` and/or `withOrganisation()`:
     * `FrontendLoggerFactory.getLogger().withComponent('UsersPage')`.
     */
    static getLogger(): IFrontendLogger {
        return new ConsoleLogger();
    }

    static async flush(): Promise<void> {
        await flushFrontendTelemetry();
    }

    static getSessionLogStorageKey(): string {
        return getFrontendSessionLogStorageKey();
    }
}

export default FrontendLoggerFactory;
